import { type ReactElement } from 'react';
import {
  useFormContext,
  useFormState,
  type FieldError,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { ChipInputField, type ChipInputFieldProps } from '../ChipInputField';

export type RHFChipInputFieldProps<T extends FieldValues> = Omit<
  ChipInputFieldProps<T>,
  'control' | 'error'
> & {
  /** When set, overrides the error read from form state (e.g. Storybook). */
  error?: FieldError;
};

/**
 * {@link ChipInputField} with `control` (and default `error`) from `useFormContext`.
 * Must be used inside {@link Form}.
 */
export const RHFChipInputField = <T extends FieldValues>({
  error: errorProp,
  ...rest
}: RHFChipInputFieldProps<T>): ReactElement => {
  const { control } = useFormContext<T>();
  const { errors } = useFormState({ control, name: [rest.name] });
  const formError = errors[rest.name] as FieldError | undefined;
  const error = errorProp ?? formError;

  return <ChipInputField {...rest} control={control} error={error} />;
};
